import { useMutation } from "@tanstack/react-query";
import { fetchApiPost } from "../utils";

// ---------------------------------------------------------------------------
// Types — a faithful mirror of the agent's `AgentResponse.to_dict()` wire
// format (lhc-data-assistant-agent: agent/types.py + SDK frames.py). The
// canonical schema lives server-side; these interfaces track it so the V2
// dashboard can render every section without a raw-JSON dump. A forward-compat
// index signature is kept on the envelope so unexpected keys survive to the
// raw view.
// ---------------------------------------------------------------------------

export type AskAssistantV2Request = {
  question: string;
  anchor_date?: string; // YYYY-MM-DD
  plan_feature?: string;
  max_retries?: number; // 0–5
};

export type AssistantV2Status = "ok" | "error" | "cannot_answer" | "needs_tool";

/** JSON-safe table cell — NaN/NaT -> null, dates -> ISO strings server-side. */
export type FrameCell = string | number | boolean | null;

/** Run-level and per-answer data warning. `FIELD_NOT_POPULATED` is a "DATA NOTE". */
export type DataWarning = {
  code: string;
  message: string;
  details: Record<string, unknown>;
};

type AnswerCommon = {
  title: string | null;
  study_id: string | null;
  version: string | null;
  warnings: DataWarning[];
  diagnostics: Record<string, unknown> | null;
  // Added at the envelope level (AgentResponse.to_dict) on top of Answer.to_dict:
  invocation: Record<string, unknown> | null;
  notes: string[];
};

export type ScalarAnswer = AnswerCommon & {
  type: "scalar";
  label: string;
  value: number | string;
  // Short tooltip describing what the (often terse, e.g. "n") label means here;
  // null when the label is unknown and the plan supplied no override.
  label_description: string | null;
};

export type FrameAnswer = AnswerCommon & {
  type: "frame";
  columns: string[];
  rows: FrameCell[][];
  // Short per-column tooltip text, keyed by column name. `{}` when none; a
  // column with no known description is simply absent (show no tooltip).
  column_descriptions: Record<string, string>;
};

export type AssistantV2Answer = ScalarAnswer | FrameAnswer;

export type AgentError = {
  code: string;
  message: string;
  details: Record<string, unknown>;
};

export type CannotAnswer = {
  kind: "missing_info" | "off_topic" | "ambiguous";
  message: string;
};

export type ToolRequest = {
  name: string;
  signature: string;
  purpose: string;
  tried: string | null;
};

export type Interpretation = {
  question_as: string | null;
  result_guide: string | null;
  nan_guide: string | null;
  has_nan: boolean;
  // Plan-supplied per-column tooltip overrides (already merged into each frame
  // answer's `column_descriptions`); `{}` when the plan supplied none.
  column_guide: Record<string, string>;
  // Whether loaded date ranges include their endpoints ("end_inclusive" /
  // "end_exclusive"); null when the run loaded no date range.
  date_range_inclusivity: string | null;
};

// --- resolutions ("show the work"). Every sub-key and inner field is optional
// (omitted when empty), so model them all as optional. ------------------------

export type ResolvedSecurity = {
  market?: string;
  group?: string;
  exchange?: string;
  instrument_symbol?: string;
  security_id?: string;
  anchor_date?: string;
  supported_intervals?: string[];
  calendar_id?: string;
};

export type ResolvedRequest = {
  name?: string;
  type?: string;
  interval?: string;
  start_date?: string;
  end_date?: string;
  range_name?: string;
  dates?: unknown;
  on_missing_date?: string;
  range_inclusivity?: string;
};

export type ResolvedSignalEventRule = {
  event_name?: string;
  field?: string;
  operator?: string;
  threshold?: unknown;
};

export type ResolvedSignal = {
  signal_id?: string;
  display_name?: string;
  version?: string;
  value_field?: string;
  interval?: string;
  event_rules?: ResolvedSignalEventRule[];
};

export type DateAdjustment = {
  frame?: string;
  requested?: string;
  used?: string;
  on_missing_date?: string;
  reason?: string; // weekend | before_data_start | after_data_end | non_trading_day | ...
};

export type OmittedDate = {
  frame?: string;
  requested?: string;
  on_missing_date?: string;
  reason?: string;
};

export type Coverage = {
  frames?: number;
  total_rows?: number;
  date_span?: [string, string];
  // Plain-language label for each coverage item above (`date_span` present only
  // when a span was computed). Use as tooltips / captions in the UI.
  descriptions?: {
    frames: string;
    total_rows: string;
    date_span?: string;
  };
};

export type Resolutions = {
  securities?: ResolvedSecurity[];
  requests?: ResolvedRequest[];
  signals?: ResolvedSignal[];
  date_adjustments?: DateAdjustment[];
  omitted_dates?: OmittedDate[];
  coverage?: Coverage;
};

export type Usage = {
  input_tokens: number | null;
  output_tokens: number | null;
  total_tokens: number | null;
  cost: number | null;
};

export type Stats = {
  model: string;
  latency_ms: number;
  retry_count: number;
  max_retries: number;
  finish_reason: string | null;
  usage: Usage | null;
};

export type StudyInvocation = {
  study_id: string;
  version: string;
  invocation: Record<string, unknown> | null;
};

export type Versions = {
  run_id: string;
  created_at: string;
  sdk_version: string;
  api_version: string;
  capability_version: string;
  studies_used: StudyInvocation[];
};

export type AssistantV2Response = {
  status: AssistantV2Status;
  question: string;
  answers: AssistantV2Answer[];
  error: AgentError | null;
  code: string | null;
  cannot_answer: CannotAnswer | null;
  tool_request: ToolRequest | null;
  interpretation: Interpretation | null;
  resolutions: Resolutions;
  defaults: Record<string, unknown>;
  warnings: DataWarning[];
  stats: Stats;
  versions: Versions | null;
  // Anything else the agent emits is preserved for the raw JSON view.
  [key: string]: unknown;
};

/**
 * POST /assistant-v2/ask — proxied through NX to mono-bun assistant-v2.
 */
export function useAskAssistantV2() {
  return useMutation({
    mutationFn: (request: AskAssistantV2Request) =>
      fetchApiPost<AssistantV2Response>("assistant-v2/ask", request),
  });
}
